import { Injectable, Injector, inject, linkedSignal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { type SupportedLanguage } from '@zag/core/common';
import { UserScopedStorageService } from './user-scoped-storage.service';

export type { SupportedLanguage } from '@zag/core/common';

const SUPPORTED_LANGUAGES: SupportedLanguage[] = ['en', 'de', 'fr', 'it'];
const LANGUAGE_STORAGE_KEY = 'smartcapacity.language';
const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

function isSupported(lang: string | undefined): lang is SupportedLanguage {
  return SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage);
}

// Circular-dep-safe: does NOT read TranslateService (which pulls the language interceptor which pulls this service).
function detectBrowserLanguage(): SupportedLanguage {
  if (typeof window === 'undefined' || typeof window.navigator === 'undefined') return DEFAULT_LANGUAGE;
  const raw = window.navigator.languages?.[0] ?? window.navigator.language;
  const stripped = raw?.split('-')[0]?.split('_')[0];
  return isSupported(stripped) ? stripped : DEFAULT_LANGUAGE;
}

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private readonly injector = inject(Injector);
  private readonly userStorage = inject(UserScopedStorageService);
  private get translateService(): TranslateService {
    return this.injector.get(TranslateService);
  }

  // Auto-recomputes on user identity change: userStorage.get transitively reads USER_ID_PROVIDER (auth.currentUser signal).
  private readonly _currentLanguage = linkedSignal<SupportedLanguage>(() => {
    const stored = this.userStorage.get(LANGUAGE_STORAGE_KEY);
    return stored && isSupported(stored) ? stored : detectBrowserLanguage();
  });

  readonly currentLanguage = this._currentLanguage.asReadonly();
  readonly supportedLanguages = SUPPORTED_LANGUAGES;

  initializeLanguage(): SupportedLanguage {
    const lang = this._currentLanguage();
    if (this.userStorage.get(LANGUAGE_STORAGE_KEY) === null) {
      this.userStorage.set(LANGUAGE_STORAGE_KEY, lang);
    }
    return lang;
  }

  setLanguage(lang: SupportedLanguage): void {
    if (!isSupported(lang)) {
      console.warn(`Language '${lang}' is not supported. Falling back to '${DEFAULT_LANGUAGE}'.`);
      lang = DEFAULT_LANGUAGE;
    }

    this._currentLanguage.set(lang);
    this.userStorage.set(LANGUAGE_STORAGE_KEY, lang);
    this.translateService.use(lang);
  }

  getCurrentLanguage(): SupportedLanguage {
    return this._currentLanguage();
  }

  getSupportedLanguages(): SupportedLanguage[] {
    return SUPPORTED_LANGUAGES;
  }

  hasStoredLanguage(): boolean {
    return this.userStorage.get(LANGUAGE_STORAGE_KEY) !== null;
  }
}
